import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	type PropsWithChildren,
} from 'react';
import type { Panel } from '@atlassian/preview-panels-api';
import { usePreviewPanelsNavigation } from '@atlassian/preview-panels-api/preview-panel-navigation';
import { addRouteListener } from '@atlassian/react-resource-router/src/controllers/router-store/index.tsx';
import { isNativeIssueViewPreviewPanel } from './isNativeIssueViewPreviewPanel.tsx';
import { JiraPreviewPanelsContext } from './JiraPreviewPanelsContext.tsx';
import {
	JiraPreviewPanelsNavigationContext,
	type NavigationListeners,
} from './JiraPreviewPanelsNavigationContext.tsx';

export const JiraPreviewPanelsNavigationProvider = ({
	children,
}: PropsWithChildren<{}>): React.JSX.Element => {
	const { navigation, subscribe } = useContext(JiraPreviewPanelsContext) || {};

	const {
		history: panelHistory,
		clearHistory,
		navigateBack,
		onPanelOpen,
	} = usePreviewPanelsNavigation();

	// Tracks browser `popstate` so we either navigate back or add the panel to history
	const didPopstateTriggerRef = useRef(false);

	// Determines whether panel navigation history will be tracked or cleared
	const didClickInsidePanelRef = useRef(false);
	const didClickInsideIssueAppRef = useRef(false);

	// URL-agnostic signal: lets consumers tell a browser back/forward apart from in-app
	// navigation so they can close their own panel on back-out using their own URL param.
	const getIsBrowserPopstateTriggered = useCallback(() => didPopstateTriggerRef.current, []);

	const markPreviewPanelClick = useCallback(() => {
		didClickInsidePanelRef.current = true;
	}, []);

	const markIssuePreviewPanelClick = useCallback(() => {
		didClickInsideIssueAppRef.current = true;
	}, []);

	useEffect(() => {
		const unsubscribeRouteListener = addRouteListener((state) => {
			if (!state.route.supportsPreviewPanel) return;
			didPopstateTriggerRef.current = state.action === 'POP';
		});

		return () => {
			didPopstateTriggerRef.current = false;
			unsubscribeRouteListener();
		};
	}, []);

	/**
	 * Preview Panel navigation history in Jira is handled globally for the following reasons:
	 * - Consumers might call `usePreviewPanelsActions().open` inside a `useEffect`,
	 *   causing unintentional behaviour on re-render if `onPanelOpen` becomes a dependency
	 * - Browser back/forward handling is decoupled from consumers of `useIssuePreviewPanel`
	 * - Limit recording navigation history to `open` events triggered inside Preview Panels
	 */
	useEffect(() => {
		const handleOpenEvent = (panelToOpen: Panel) => {
			const panelHistoryIndex = panelHistory.findIndex(
				(panelHistoryItem) => panelHistoryItem.id === panelToOpen.id,
			);

			const shouldNavigateBack =
				didPopstateTriggerRef.current &&
				panelHistoryIndex > -1 &&
				panelHistoryIndex === panelHistory.length - 1;

			if (shouldNavigateBack) {
				// If browser back/forward is triggered and the next panel opened was the last history item, remove it from history instead
				navigateBack();
			} else if (!didClickInsidePanelRef.current) {
				// Reset navigation history if event was triggered outside of the current panel
				clearHistory();
			} else if (
				!isNativeIssueViewPreviewPanel(panelToOpen) ||
				// Excludes any navigation handling in the Issue Preview Panel header
				didClickInsideIssueAppRef.current
			) {
				// Record navigation history only if the event triggered inside the current panel
				// is from a non-issue panel OR the issue app wrapper
				onPanelOpen();

				if (navigation) {
					navigation.onNavigateAway.current?.(panelToOpen);
					// Discard so we can handle the next panel's `onNavigateAway`
					navigation.onNavigateAway.current = undefined;
				}
			}
		};

		const unsubscribePreviewPanelListener = subscribe?.((event) => {
			if (event.action === 'open') {
				handleOpenEvent(event.panel);

				// Clear refs only after `open` events since `close` can also trigger in the same tick
				didPopstateTriggerRef.current = false;
				didClickInsidePanelRef.current = false;
				didClickInsideIssueAppRef.current = false;
			}
		});

		return () => unsubscribePreviewPanelListener?.();
	}, [navigation, panelHistory, clearHistory, navigateBack, onPanelOpen, subscribe]);

	const setPreviewPanelNavigation = useCallback(
		(listeners: NavigationListeners) => {
			if (navigation == null) return;

			if (listeners.onNavigatePrevious) {
				navigation.onNavigatePrevious.current = listeners.onNavigatePrevious;
			}
			if (listeners.onNavigateNext) {
				navigation.onNavigateNext.current = listeners.onNavigateNext;
			}
			if (listeners.onNavigateAway) {
				navigation.onNavigateAway.current = listeners.onNavigateAway;
			}
		},
		[navigation],
	);

	const context = useMemo(
		() => ({
			getIsBrowserPopstateTriggered,
			setPreviewPanelNavigation,
			markPreviewPanelClick,
			markIssuePreviewPanelClick,
		}),
		[
			getIsBrowserPopstateTriggered,
			setPreviewPanelNavigation,
			markPreviewPanelClick,
			markIssuePreviewPanelClick,
		],
	);

	return (
		<JiraPreviewPanelsNavigationContext.Provider value={context}>
			{children}
		</JiraPreviewPanelsNavigationContext.Provider>
	);
};
